import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { FilterSet } from '#server/models';
import { trackGAEvent } from '@/services';

export const EARLIEST_DATE = '0001-01-01';
export const LATEST_DATE = '9999-12-31';

type NumberRange = [number, number];
type DateRange = [string, string];
type AnyRange = NumberRange | DateRange;

export type ActiveFilters = Record<string, string[] | AnyRange>;

const isNumberRange = (v: unknown): v is NumberRange =>
  Array.isArray(v) && v.length === 2 && typeof v[0] === 'number' && typeof v[1] === 'number';

const isDateISO = (s: unknown): s is string => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
const isDateRange = (v: unknown): v is DateRange =>
  Array.isArray(v) && v.length === 2 && isDateISO(v[0]) && isDateISO(v[1]);

const clamp = (x: number, lo: number, hi: number) => Math.min(Math.max(x, lo), hi);

const clampDate = (d: string, lo: string, hi: string) => {
  // Don't clamp empty strings - allow independent date setting
  if (!d || d === '') {
    return d;
  }

  return d < lo ? lo : d > hi ? hi : d;
};

interface FilterState {
  facetFilters: Record<string, ActiveFilters>;
  activeFilters: ActiveFilters;
  currentFacet: string | null;
  region: 'domestic' | 'international';
  currentFilterSet: FilterSet | null;
  actions: {
    setCurrentFacet: (facet: string, filterSet: FilterSet) => void;
    setRegion: (region: 'domestic' | 'international') => void;
    toggleFilterOption: (category: string, option: string) => void;
    setSliderValue: (category: string, value: number[] | NumberRange) => void;
    setDateRangeValue: (category: string, value: string[] | DateRange) => void;
    clearFilter: (category: string) => void;
    clearFilters: () => void;
  };
}

export const useFilterStore = create<FilterState>()(
  persist(
    (set, get) => ({
      facetFilters: {},
      activeFilters: {},
      currentFacet: null,
      region: 'domestic',
      currentFilterSet: null,
      actions: {
        setRegion: (region) => set({ region }),
        setCurrentFacet: (facet, filterSet) => {
          const { facetFilters, region } = get();

          let currentFilterSet = filterSet;

          currentFilterSet = filterSet.filter((c) => c.region === region || !c.region);

          const existingFilters = facetFilters[facet];

          if (existingFilters) {
            set({ currentFacet: facet, currentFilterSet: currentFilterSet, activeFilters: existingFilters });
          } else {
            set({
              currentFacet: facet,
              currentFilterSet: currentFilterSet,
              activeFilters: {},
              facetFilters: { ...facetFilters, [facet]: {} },
            });
          }
        },

        toggleFilterOption: (category, option) => {
          set((state) => {
            if (!state.currentFacet) return {};

            const current = state.activeFilters[category];
            const currentOptions = isNumberRange(current) || isDateRange(current) ? [] : (current ?? []);
            let newOptions = [];
            if (currentOptions.includes(option)) {
              newOptions = currentOptions.filter((o) => o !== option);
            } else {
              newOptions = [...currentOptions, option];
              trackGAEvent({
                event: 'search_filter_applied',
                filter_category: category,
                filter_option: option,
              });
            }

            const newActiveFilters = { ...state.activeFilters, [category]: newOptions };
            const facetKey = state.currentFacet;
            const newFacetFilters = { ...state.facetFilters, [facetKey]: newActiveFilters };

            return { activeFilters: newActiveFilters, facetFilters: newFacetFilters };
          });
        },

        setSliderValue: (category, value) => {
          set((state) => {
            if (!state.currentFacet) return {};

            const cfg = state.currentFilterSet?.find((c) => c.title === category);
            const lo = cfg?.min ?? 0;
            const hi = cfg?.max ?? 100;
            const [a, b] = value as number[];
            const next: NumberRange = [clamp(a ?? lo, lo, hi), clamp(b ?? hi, lo, hi)];

            trackGAEvent({ event: 'search_filter_applied', filter_category: category, filter_option: next.toString() });

            const newActiveFilters = { ...state.activeFilters, [category]: next };
            const facetKey = state.currentFacet;
            const newFacetFilters = { ...state.facetFilters, [facetKey]: newActiveFilters };

            return { activeFilters: newActiveFilters, facetFilters: newFacetFilters };
          });
        },

        setDateRangeValue: (category, value) => {
          set((state) => {
            if (!state.currentFacet) return {};

            const cfg = state.currentFilterSet?.find((c) => c.title === category);
            const lo = (cfg as any)?.minDate ?? EARLIEST_DATE;
            const hi = (cfg as any)?.maxDate ?? LATEST_DATE;
            const [a, b] = value as string[];
            const next: DateRange = [clampDate(a ?? lo, lo, hi), clampDate(b ?? hi, lo, hi)];

            trackGAEvent({ event: 'search_filter_applied', filter_category: category, filter_option: next.toString() });

            const newActiveFilters = { ...state.activeFilters, [category]: next };
            const facetKey = state.currentFacet;
            const newFacetFilters = { ...state.facetFilters, [facetKey]: newActiveFilters };

            return { activeFilters: newActiveFilters, facetFilters: newFacetFilters };
          });
        },
        clearFilter: (category) =>
          set((state) => {
            if (!state.currentFacet) return {};
            const { [category]: _, ...newActiveFilters } = state.activeFilters;
            const facetKey = state.currentFacet;
            const newFacetFilters = { ...state.facetFilters, [facetKey]: newActiveFilters };
            return { activeFilters: newActiveFilters, facetFilters: newFacetFilters };
          }),
        clearFilters: () =>
          set((state) => {
            if (!state.currentFacet) return {};
            const newActiveFilters = {};
            const facetKey = state.currentFacet;
            const newFacetFilters = { ...state.facetFilters, [facetKey]: newActiveFilters };
            return { activeFilters: newActiveFilters, facetFilters: newFacetFilters };
          }),
      },
    }),
    {
      name: 'search-filters-v1',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({
        facetFilters: s.facetFilters,
        activeFilters: s.activeFilters,
        currentFacet: s.currentFacet,
        region: s.region,
      }),
      version: 1,
      migrate: (persisted, version) => {
        return persisted as any;
      },
    },
  ),
);

export const useFilterActions = () => useFilterStore((s) => s.actions);
